<template>
  <a
    class="term-card"
    :class="{
      'is-selected': term.isSelected,
      'is-disabled': disabled,
      'has-error': hasError
    }"
    @click="onInput"
  >
    <div class="term-card-head">
      <strong class="has-text-grey-dark">{{ term.name }}</strong>
    </div>
    <div class="term-card-body">
      <!-- Was price (if applicable) -->
      <template v-if="!!term.wasPriceFormatted">
        <p class="is-size-8 has-text-centered has-text-danger">
          <i18n tag="del" path="_.was_price">
            <template #price>
              <span>{{ term.wasPriceFormatted }}</span>
            </template>
          </i18n>
        </p>
      </template>
      <!-- Now price -->
      <p class="term-card-price" :class="showWasPriceAfter && 'is-first'">
        <editable-price
          :key="term.key"
          v-model="$product.prices[term.key]"
          position="is-bottom-right"
          :can-edit="term.isCustomisable"
          :currency="$product.currency"
          :is-disabled="disabled"
          :original-value-formatted="term.originalPriceFormatted"
          :original-value="term.originalPrice"
          :value-formatted="term.nowPriceFormatted"
          :instruction="
            $t('_sentence.override_selling_price', {
              object: $t('_.billing_term').toString().toLowerCase(),
              taxType: $tc('_.price_tax_type_switch', $product.taxType)
            })
          "
          @input="
            $event === term.originalPrice && $delete($product.prices, term.key)
          "
        >
          <template #trigger>
            <u-link :key="term.nowPriceFormatted">{{
              term.nowPriceFormatted
            }}</u-link>
          </template>
        </editable-price>
      </p>
      <!-- Monthly comparison (eg. £1.50 a month) -->
      <p class="is-size-8 has-text-centered has-text-grey-light">
        <template v-if="!term.hasCustomPrice && term.months > 1">
          <em>
            <i18n path="_.monthly_from_price">
              <template #price>{{ term.monthlyComparison }}</template>
            </i18n>
          </em>
        </template>
        <span v-else>–</span>
      </p>
      <!-- 'Custom price' tag -->
      <template v-if="term.hasCustomPrice">
        <p class="term-card-promos">
          <b-tag type="is-caution">
            <i18n path="_.custom_price" />
          </b-tag>
        </p>
      </template>
      <!-- Promotion tags (eg. Black Friday Sale) -->
      <template v-else-if="term.hasDiscountedPrice">
        <p class="term-card-promos">
          <b-tooltip
            v-for="(promo, pI) in term.mappedPromotions"
            :key="`promo-${pI}`"
            :label="promo.tooltip"
            :size="term.hasMixedPromotions ? 'is-medium' : 'is-small'"
            type="is-danger"
            multilined
          >
            <b-tag type="is-danger has-cursor-help">{{ promo.label }}</b-tag>
          </b-tooltip>
        </p>
      </template>
    </div>
  </a>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import Context from "@/mixins/context";
import {
  BrandConfigKeys,
  PriceBeforeDiscountPositions
} from "@/data/constants";
import type { PropType } from "vue";
import type {
  IAvailableTermMapped,
  IProductConfigProviderData
} from "@/models/providers/products";

export default defineComponent({
  name: "ProductTermCard",
  components: {
    "editable-price": () =>
      import(
        "@/components/app/global/baskets/basket/productView/editablePrice.vue"
      )
  },
  mixins: [Context],
  inject: {
    productConfigProviderData: { from: "productConfigProviderData" }
  },
  props: {
    term: { type: Object as PropType<IAvailableTermMapped>, required: true },
    disabled: { type: Boolean, default: false },
    hasError: { type: Boolean, default: false }
  },
  computed: {
    $product(): IProductConfigProviderData {
      return this.productConfigProviderData();
    },
    showWasPriceAfter() {
      return (
        this.$store.getters["brand/config"][
          BrandConfigKeys.UI_PRICE_BEFORE_DISCOUNT_POSITION
        ] === PriceBeforeDiscountPositions.AFTER
      );
    }
  },
  methods: {
    onInput() {
      if (this.disabled) return;
      this.$emit("input", this.term.months);
    }
  }
});
</script>

<style lang="scss" scoped>
a.term-card {
  display: grid;
  grid-template-rows: auto 1fr;
  color: $text;

  .term-card-head {
    display: flex;
    justify-content: center;
    padding: 0.5rem 0.75rem;
    border: solid $border;
    border-width: 1px 1px 0;
    border-radius: $radius-large $radius-large 0 0;
    transition: border-color ease-out 0.12s;

    .b-radio {
      margin: 0;
      &::v-deep .control-label:empty {
        display: none;
      }
    }
  }

  .term-card-body {
    padding: 1rem;
    display: grid;
    align-content: center;
    border: 1px solid $border;
    border-top-color: $border !important;
    border-radius: 0 0 $radius-large $radius-large;
    transition: border-color ease-out 0.12s;

    .term-card-price {
      font-size: $size-4;
      text-align: center;
      &.is-first {
        order: -1;
      }
    }

    .term-card-promos {
      display: flex;
      justify-content: center;
      flex-wrap: wrap;
      gap: 0.25rem;
      margin-top: 0.5rem;
    }
  }

  // STATE: Error

  &.has-error {
    .term-card-head,
    .term-card-body {
      border-color: $danger;
    }
  }

  // STATE: Disabled

  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  // STATE: Hover

  &:not(.is-disabled):not(.is-selected):hover {
    .term-card-head,
    .term-card-body {
      border-color: $grey-light;
    }
  }

  // STATE: Selected

  &.is-selected {
    position: relative;
    box-shadow: 0 0 0.5rem 0 rgba($black, 0.1);

    &::after {
      content: "\F058";
      font-family: $family-icon;
      font-size: 1.375rem;
      font-weight: 900;
      line-height: 1;
      font-style: normal;
      font-variant: normal;
      text-rendering: auto;
      -webkit-font-smoothing: antialiased;
      position: absolute;
      top: -0.25rem;
      right: -0.25rem;
      border-radius: 50%;
      color: $link;
      background: $white;
    }

    .term-card-head {
      border-color: $info;
    }

    .term-card-body {
      border-color: $info;
      background-color: mix($info, $white, 4%);
    }
  }
}
</style>
